// src/features/dzban/components/VoteBattle.jsx
// DARK THEME VERSION - Glassmorphism battle cards
// Component showing 2 spots side-by-side for voting

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ThumbsDown } from 'lucide-react';

export const VoteBattle = ({ spot1, spot2, onVote, disabled }) => {
  const navigate = useNavigate();
  
  if (!spot1 || !spot2) return null;

  const SpotCard = ({ spot, onVoteClick }) => {
    const imageUrl = spot.images?.[0] || spot.imageUrl || 'https://via.placeholder.com/400x300';
    
    return (
      <div 
        className="flex-1 rounded-xl lg:rounded-2xl overflow-hidden border border-gray-800 hover:border-orange-500/50 transition-all duration-300 group"
        style={{
          backgroundColor: '#1f1f1f',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          borderBottom: '4px solid #ff6600'
        }}
      >
        {/* Image - responsive aspect ratio - CLICKABLE with hover effects */}
        <button
          onClick={() => navigate(`/pojazd/${spot.id}`)}
          className="relative aspect-[4/3] lg:aspect-video bg-gray-900 w-full cursor-pointer overflow-hidden"
        >
          <img 
            src={imageUrl} 
            alt={`${spot.brand || 'Auto'} ${spot.plate}`}
            className="w-full h-full object-cover transition-all duration-400 filter contrast-125 saturate-110 grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105"
            style={{
              filter: 'contrast(1.25) saturate(1.1) grayscale(30%)',
            }}
          />
          
          {/* Hover glow overlay */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none"
            style={{
              boxShadow: '0 0 0 2px rgba(255, 102, 0, 0.5) inset'
            }}
          />
          
          {/* Alert badge */}
          {spot.isAlert && (
            <span 
              className="absolute top-1.5 right-1.5 lg:top-3 lg:right-3 bg-red-600 text-white px-1.5 py-0.5 lg:px-3 lg:py-1 rounded text-[10px] lg:text-xs font-bold animate-pulse pointer-events-none"
              style={{ boxShadow: '0 0 15px rgba(220, 38, 38, 0.6)' }}
            >
              ⚠️ ALERT
            </span>
          )}
        </button>

        {/* Info box - dark glassmorphism */}
        <div 
          className="p-3 lg:p-5 bg-[#0a0a0a]/40 backdrop-blur-sm border-t border-gray-800"
        >
          <div className="text-center mb-3 lg:mb-4">
            {/* Plate - white with glow */}
            <div 
              className="text-lg lg:text-2xl font-black text-white mb-1 lg:mb-2 uppercase tracking-wide"
              style={{ 
                textShadow: '0 0 10px rgba(255, 255, 255, 0.2)',
                fontFamily: '"Bebas Neue", "Impact", sans-serif'
              }}
            >
              {spot.plate || 'BRAK'}
            </div>
            
            {/* Brand - gray */}
            <div className="text-xs lg:text-sm text-gray-400 uppercase tracking-wider">
              {spot.brand || 'Nieznana marka'}
            </div>
            
            {/* Location - orange accent */}
            <div className="text-[10px] lg:text-xs text-orange-400 mt-1 lg:mt-2 font-semibold">
              📍 {spot.locationName || spot.location || 'Polska'}
            </div>
          </div>

          {/* Vote button - RED GRADIENT with GLOW */}
          <button
            onClick={onVoteClick}
            disabled={disabled}
            className={`w-full py-3 lg:py-4 rounded-lg font-bold text-white transition-all flex items-center justify-center gap-2 text-sm lg:text-base uppercase tracking-wide ${
              disabled 
                ? 'bg-gray-700 cursor-not-allowed opacity-50' 
                : 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 active:scale-95 hover:-translate-y-1'
            }`}
            style={!disabled ? {
              boxShadow: '0 0 20px rgba(220, 38, 38, 0.5)',
            } : {}}
            onMouseEnter={(e) => {
              if (!disabled) {
                e.currentTarget.style.boxShadow = '0 0 30px rgba(220, 38, 38, 0.7)';
              }
            }}
            onMouseLeave={(e) => {
              if (!disabled) {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(220, 38, 38, 0.5)';
              }
            }}
          >
            <ThumbsDown size={16} className="lg:hidden" />
            <ThumbsDown size={20} className="hidden lg:block" />
            GŁOSUJ
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex gap-3 lg:gap-8 items-center">
      <SpotCard 
        spot={spot1} 
        onVoteClick={() => onVote(spot1.id, spot2.id)} 
        disabled={disabled}
      />
      
      {/* VS divider - ANIMATED with ping effect */}
      <div className="flex flex-col items-center justify-center relative flex-shrink-0">
        {/* Animated outer ring (ping effect) */}
        <div 
          className="absolute w-14 h-14 lg:w-20 lg:h-20 bg-orange-500/50 rounded-full animate-ping"
          style={{ animationDuration: '1.5s' }}
        />
        
        {/* Main VS badge */}
        <div 
          className="relative w-12 h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-red-600 to-orange-600 rounded-full flex items-center justify-center text-white font-black text-base lg:text-xl shadow-lg border-4 border-black z-10"
          style={{ 
            boxShadow: '0 0 30px rgba(255, 100, 0, 0.8)',
            fontFamily: '"Bebas Neue", "Impact", sans-serif'
          }}
        >
          VS
        </div>
      </div>
      
      <SpotCard 
        spot={spot2} 
        onVoteClick={() => onVote(spot2.id, spot1.id)} 
        disabled={disabled}
      />
    </div>
  );
};
